import { Parser } from 'html-to-react'
import { useRef, useState } from 'react'
import CaseLawReferenceCitatorTab from '@/citator/views/CaseLawReferenceCitatorTab'
import { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { CaselawReferenceMetadataResponseV3 } from '../schema/caselaw-reference-viewer.schema'
import { trackRiaSourceViewerTabChanged } from '@/analytics/utils/ria-source-analytics'
import { RiaViewerTab } from '@/analytics/enums/ria.enums'
import type { RiaSourceContext } from '@/analytics/utils/ria-source-analytics'
import { HtmlReferenceLoadingSkeleton } from '@/chat-common/components/reference-viewer/caselaw/CaseLawOpinionLoadingSkeleton'

type StandaloneReferenceViewCaselawTabsProps = {
  metadata: CaselawReferenceMetadataResponseV3
  /** Sanitized source HTML (or `null`/`undefined` while still loading in the parent). */
  sanitizedHtml: string | null | undefined
  isLoadingHtml: boolean
  isErrorHtml: boolean
  refetchHtml: () => void
  /** Analytics context (RIA), derived by the parent page. */
  analyticsContext: RiaSourceContext | null
}

export default function StandaloneReferenceViewCaselawTabs({
  metadata,
  sanitizedHtml,
  isLoadingHtml,
  isErrorHtml,
  refetchHtml,
  analyticsContext,
}: StandaloneReferenceViewCaselawTabsProps) {
  const { trackEvent } = useAnalytics()

  // Local state
  const containerRef = useRef<HTMLDivElement>(null)
  const [activeTab, setActiveTab] = useState<CaseLawReferenceTab>(CaseLawReferenceTab.source)

  // Styles for tabs
  const baseTabStyles = 'grow w-1/2 min-h-[52px] py-3 border-b-2 text-center text-[15px] font-semibold font-rethink-sans'
  const inactiveTabStyles = `${baseTabStyles} border-transparent text-brand-neutral-500 hover:bg-brand-neutral-100 hover:text-brand-500`
  const activeTabStyles = `${baseTabStyles} border-brand-800 text-brand-800`

  // Handle tab change
  const trackTabChange = (nextTab: CaseLawReferenceTab) => {
    if (!analyticsContext) {
      return
    }

    const toTab = nextTab === CaseLawReferenceTab.citator ? RiaViewerTab.Citator : RiaViewerTab.SourceView
    const fromTab = activeTab === CaseLawReferenceTab.citator ? RiaViewerTab.Citator : RiaViewerTab.SourceView

    if (toTab === fromTab) {
      return
    }

    trackRiaSourceViewerTabChanged({
      trackEvent,
      context: analyticsContext,
      fromTab,
      toTab,
    })
  }

  function handleActiveTabChange(tab: CaseLawReferenceTab) {
    trackTabChange(tab)
    if (tab !== activeTab) {
      setActiveTab(tab)
    }
  }

  // Configure the HTML parser
  const htmlParser = Parser()

  return (
    <>
      {/* Tabs */}
      <div className="border-b">
        <nav className="w-full flex items-center" aria-label="Tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === CaseLawReferenceTab.source}
            onClick={() => {
              handleActiveTabChange(CaseLawReferenceTab.source)
            }}
            className={activeTab == CaseLawReferenceTab.source ? activeTabStyles : inactiveTabStyles}
          >
            Opinion
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === CaseLawReferenceTab.citator}
            onClick={() => {
              handleActiveTabChange(CaseLawReferenceTab.citator)
            }}
            className={activeTab == CaseLawReferenceTab.citator ? activeTabStyles : inactiveTabStyles}
          >
            Citation Analysis
          </button>
        </nav>
      </div>
      {/* Render Source */}
      {activeTab === CaseLawReferenceTab.source && (
        <div className="reference-view-html">
          {isLoadingHtml && <HtmlReferenceLoadingSkeleton className="p-0" />}

          {(isErrorHtml || (!sanitizedHtml && !isLoadingHtml)) && (
            <div className="p-4">
              <div className="text-red-700 text-sm">Error fetching data.</div>
              <button
                className="flex-auto items-center rounded-md bg-brand-500 border-[1px] border-brand-500 px-2 py-1 my-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400"
                onClick={() => refetchHtml()}
              >
                Retry
              </button>
            </div>
          )}

          {sanitizedHtml && !isLoadingHtml && (
            <div id="case-law-source" ref={containerRef} className="view-container bg-white w-full border border-brand-neutral-200 p-4">
              {htmlParser.parse(sanitizedHtml)}
            </div>
          )}
        </div>
      )}

      {/* Render Citator */}
      {activeTab === CaseLawReferenceTab.citator && <CaseLawReferenceCitatorTab className="flex-1" metadata={metadata} analyticsContext={analyticsContext} />}
    </>
  )
}
